import React, { Suspense, useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "react-avatar";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { colors, NotificationIcon, VerticalHr } from "components";
import { useTenant } from "multitenancy/config";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { setContainerName } from "store/modules/profile/actions";
import { useAppSelector } from "store/rootReducer";

import { ProfileMenu } from "features/ProfileMenu";
import { IProfile } from "features/ProfileMenu/interface";

import ProfileModal from "components/ProfileModal";
import {
  avatarStyle,
  HeaderPictureContainer,
  PictureContainer,
} from "components/ProfileModal/style";

import { UserCustomerProfileService } from "services/user_customer_profile";
import { UserCustomerProfile } from "services/user_customer_profile/types";

import CloundDownloadIcon from "assets/icons/cloundDownloadIcon";
import DashboardIcon from "assets/icons/dashboardIcon";

import { FORMS_HEARING } from "utils/entities/permission";

import { IHeader } from "./interface";
import {
  ActionButtonsContainer,
  AvatarWrapper,
  BoxMenuLeftFullScrean,
  ButtonTabGroupFixed,
  Container,
  DivLogo,
  HeaderActionButton,
  Logo,
  ProfileSelectorWrapper,
  RightSection,
  Separator,
} from "./styles";
import axios from "axios";

interface IHeaderCustom extends IHeader {
  qntNotifications: number;
  onClickNotifications: () => unknown;
  onClickDownloadApp: () => unknown;
  menuIndexToShow: number | undefined;
  setOpenPreferencesDrawer: (open: boolean) => any;
}

export const Header: React.FC<IHeaderCustom> = ({
  id,
  logo,
  menus,
  api,
  menuIndexToShow,
  setSelectedProfile,
  qntNotifications,
  onClickNotifications,
  onClickDownloadApp,
  setOpenPreferencesDrawer,
}) => {
  const { t } = useTranslation();
  const [load, setLoad] = useState<boolean>(false);
  const [isOpenSideModal, setIsOpenSideModal] = useState(false);
  const [profileList, setProfileList] = useState<IProfile[]>([]);
  const { addToast } = useToast();
  const { tenant } = useTenant();
  const [userCustomerProfiles, setUserCustomerProfiles] = useState<
    UserCustomerProfile[]
  >([]);

  const appName = tenant.labels.appName;

  const { userCustomerProfile } = useAppSelector((state) => state.profile);

  const { user } = useAppSelector((state) => state.user);
  const { accessToken } = useAppSelector((state) => state.auth);

  const { export: exportDownloads } = usePermissions(
    FORMS_HEARING.HEARING_FILE,
  );

  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();

  const dashPath = "/dashboard";
  const isOnDash = location.pathname.startsWith(dashPath);
  const handleDashboardClick: React.MouseEventHandler<HTMLAnchorElement> = (
    e,
  ) => {
    if (isOnDash) {
      e.preventDefault();
      navigate(0);
    }
  };

  async function profileServicesExemple() {
    if (user?.customerId) {
      const { data } = await api.get<any>(
        `/v1/UserCustomerProfile/GetUserCustomerProfile`,
      );

      if (data.ok) {
        return data.data;
      }
    }
  }

  const menuList = menus;

  const fetchData = async () => {
    try {
      setLoad(true);
      const profilesList = await profileServicesExemple();
      const existsMaster =
        profilesList?.find((x: any) => x.profile?.toLowerCase() === "Master") ||
        [];
      const filterWithOutMasterProfiles =
        profilesList?.filter(
          (x: any) => x.profile?.toLowerCase() !== "Master",
        ) || [];
      const profiles = existsMaster
        ? [existsMaster, ...filterWithOutMasterProfiles]
        : filterWithOutMasterProfiles || [];

      setProfileList(profiles);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return;
      }

      addToast({
        type: "error",
        title: "Erro no Perfil",
        description: `Ocorreu um erro ao verificar o perfil.`,
      });
    } finally {
      setLoad(false);
    }
  };

  const searchProfiles = async () => {
    if (!user) {
      return;
    }

    if (!accessToken || !user || !user?.customerId) {
      return;
    }

    const response = await UserCustomerProfileService.getAllByUserCustomer(
      accessToken,
      user?.id,
    );

    if (!response.data || !response.status) return;
    setUserCustomerProfiles(response.data);

    dispatch(setContainerName(response?.data[0]?.containerName?.toString()));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    searchProfiles();
  }, [user]);

  const handleSelectProfile = (profile: any) => {
    setSelectedProfile(profile);
  };

  const handleOpenProfileModal = () => {
    setIsOpenSideModal(true);
  };

  return !load ? (
    <Container>
      <Suspense fallback={<div>Loading...</div>}>
        <AppBar
          id={id}
          position="static"
          sx={{
            flexGrow: 1,
            flexDirection: "column",
            justifyContent: "center",
            display: "flex",
            width: "100%",
            height: "60px",
            background: colors.default_base_white,
            boxShadow: "none",
            paddingRight: "15px",
          }}
        >
          <Toolbar disableGutters>
            <BoxMenuLeftFullScrean>
              <DivLogo>
                <Logo src={logo} />
              </DivLogo>
              <VerticalHr height="40px" />
              <ButtonTabGroupFixed
                menuList={menuList}
                indexToSelect={menuIndexToShow}
                navigate={navigate}
              />
            </BoxMenuLeftFullScrean>
            <RightSection>
              <ProfileSelectorWrapper>
                <ProfileMenu
                  profiles={profileList}
                  selected={userCustomerProfile}
                  handleSelectProfile={handleSelectProfile}
                />
              </ProfileSelectorWrapper>

              <Separator>
                <VerticalHr height="40px" />
              </Separator>

              <ActionButtonsContainer>
                <HeaderActionButton>
                  <Tooltip
                    title={
                      isOnDash ? t("dashboard.reload") : t("dashboard.title")
                    }
                  >
                    <Link
                      to={dashPath}
                      onClick={handleDashboardClick}
                      aria-current={isOnDash ? "page" : undefined}
                    >
                      <DashboardIcon />
                    </Link>
                  </Tooltip>
                </HeaderActionButton>

                <HeaderActionButton>
                  <Tooltip title={t("notifications.title")}>
                    <div>
                      <NotificationIcon
                        qtdNotification={qntNotifications}
                        tooltiTitle=""
                        onClick={onClickNotifications}
                      />
                    </div>
                  </Tooltip>
                </HeaderActionButton>

                {exportDownloads && (
                  <HeaderActionButton onClick={onClickDownloadApp}>
                    <Tooltip title={t("common.download")}>
                      <div>
                        <CloundDownloadIcon />
                      </div>
                    </Tooltip>
                  </HeaderActionButton>
                )}

                <Separator>
                  <VerticalHr height="40px" />
                </Separator>
              </ActionButtonsContainer>

              <AvatarWrapper
                id="avatar_container"
                onClick={handleOpenProfileModal}
              >
                {userCustomerProfiles && user?.picture ? (
                  <HeaderPictureContainer>
                    <PictureContainer
                      src={`data:image/png;base64,${user.picture}`}
                    />
                  </HeaderPictureContainer>
                ) : (
                  <Avatar
                    name={user?.name ? user.name : "user test"}
                    size="38"
                    round={true}
                    style={{ ...avatarStyle }}
                  />
                )}
              </AvatarWrapper>

              {!!userCustomerProfiles?.length && user ? (
                <ProfileModal
                  setOpenPreferencesDrawer={setOpenPreferencesDrawer}
                  isOpen={isOpenSideModal}
                  customer={userCustomerProfiles[0]?.customer}
                  onClose={() => setIsOpenSideModal(false)}
                  profile={userCustomerProfiles[0].profile}
                />
              ) : null}
            </RightSection>
          </Toolbar>
        </AppBar>
      </Suspense>
    </Container>
  ) : null;
};
